import styled, { css } from 'styled-components';

import { BREAKPOINTS } from '@shared/styled-system';

export enum MODAL_SIZE {
	MEDIUM = 'medium',
	LARGE = 'large'
}

export enum TITLE_POSITION {
	START = 'start',
	CENTER = 'center',
	END = 'end'
}

export enum BACKGROUND_COLOR {
	dark = 'dark',
	neutral = 'neutral'
}

export type ModalProps = {
	$size: Lowercase<keyof typeof MODAL_SIZE>;
	$titlePosition: Lowercase<keyof typeof TITLE_POSITION>;
	$backgroundColor: Lowercase<keyof typeof BACKGROUND_COLOR>;
	$maxWidth?: number;
	$hasShadow?: boolean;
	$hasPadding?: boolean;
};

export const CloseButton = styled.div`
	position: absolute;
	display: flex;
	align-items: center;
	justify-content: center;
	top: 50%;
	transform: translateY(-50%);
	right: 16px;
	width: 32px;
	height: 32px;
	cursor: pointer;
	z-index: 2;
`;

export const IconWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;

	& > svg {
		fill: #e3e2e2;
	}
`;

export const BackButton = styled.div`
	position: absolute;
	left: 16px;
	top: 50%;
	transform: translateY(-50%);
	cursor: pointer;
	height: 24px;
	z-index: 2;

	svg {
		width: 24px;
		height: 24px;
		fill: ${({ theme }) => theme.colors.icon.chevron};
	}
`;

export const ModalHeader = styled.div<{ $background?: string }>`
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: center;

	border-radius: 12px 12px 0 0;
	overflow: hidden;
	min-height: 56px;
	background-color: ${({ theme }) => theme.colors.background.main};

	${({ $background }) =>
		$background
			? css`
					background-image: url('${$background}');
					background-size: cover;
					background-position: bottom right;

					&:after {
						content: '';
						position: absolute;
						left: 0;
						bottom: -1px;
						width: 100%;
						height: 187px;
						background: linear-gradient(180deg, rgba(23, 23, 22, 0) 1.24%, rgba(23, 23, 22, 0.72) 101.24%);
					}
				`
			: ''}
`;

export const Title = styled.div`
	font-weight: 600;
	color: ${({ theme }) => theme.colors.text.title};
	position: relative;
	font-size: 20px;
	line-height: 27px;
	z-index: 1;

	padding: 0 18px;
	width: 100%;
	text-align: inherit;

	* {
		font-size: 20px;
		line-height: 27px;
		color: ${({ theme }) => theme.colors.text.title};
		margin: 0;
	}
`;

export const ModalContent = styled.div<{ $hasHeader?: boolean; $noBorder?: boolean }>`
	border-top: ${({ theme, $hasHeader, $noBorder }) =>
		$hasHeader && !$noBorder ? `1px solid ${theme.borders.border200}` : 'none'};
	color: ${({ theme }) => theme.colors.text.body};
	height: 100%;
	overflow-y: auto;
	overflow-x: hidden;
`;

export const Overlay = styled.div<{
	$hasOverlay?: boolean;
	$backgroundColor?: Lowercase<keyof typeof BACKGROUND_COLOR>;
}>`
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	z-index: 1000;
	backdrop-filter: blur(4px);
	background-color: ${({ theme, $hasOverlay, $backgroundColor }) =>
		$hasOverlay
			? $backgroundColor === 'dark'
				? theme.colors.background.modalViewSecondary
				: theme.colors.background.modalView
			: 'transparent'};
`;

const sizeMixin = (props: ModalProps) => {
	if (props.$hasPadding && props.$size === MODAL_SIZE.MEDIUM) {
		return css`
			${ModalHeader} {
				font-size: 20px;
				padding: 12px 0;
			}

			${ModalContent} {
				padding: 12px 12px 8px;
			}
		`;
	}
	if (props.$hasPadding && props.$size === MODAL_SIZE.LARGE) {
		return css`
			${ModalHeader} {
				font-size: 24px;
				padding: 16px 0;
			}

			${ModalContent} {
				padding: 16px 16px 8px;
			}
		`;
	}
};

export const ModalWrapper = styled.div<ModalProps>`
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 1001;
	border-radius: 12px;
	color: ${({ theme }) => theme.colors.text.body};
	max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : '600px')};
	max-height: calc(100vh - 140px);
	overflow: hidden;
	width: 100%;
	box-shadow: ${({ $hasShadow }) => ($hasShadow ? '0px 8px 16px 0px rgba(0, 0, 0, 0.24)' : 'none')};
	display: flex;
	flex-direction: column;

	${sizeMixin}

	${({ $backgroundColor }) => {
		if ($backgroundColor === 'dark') {
			return css`
				background-color: ${({ theme }) => theme.colors.background.secondary};

				${ModalHeader} {
					background-color: ${({ theme }) => theme.colors.background.secondary};
				}
			`;
		}
		if ($backgroundColor === 'neutral') {
			return css`
				background-color: ${({ theme }) => theme.colors.background.main};
			`;
		}
	}}

  ${ModalHeader} {
		align-items: ${({ $titlePosition }) =>
			$titlePosition === TITLE_POSITION.START
				? 'flex-start'
				: $titlePosition === TITLE_POSITION.END
					? 'flex-end'
					: 'center'};

		text-align: ${({ $titlePosition }) =>
			$titlePosition === TITLE_POSITION.START ? 'left' : $titlePosition === TITLE_POSITION.END ? 'right' : 'center'};
	}

	@media (${BREAKPOINTS.MOBILE}) {
		width: calc(100% - 20px);
	}
`;
